import { IEvent, IAlert, IWSAlert, NotificationSeverity, NotificationAction } from '../../models/types';
import { notificationTypesData } from '../../constants';
import { paths } from '../../routes/Root/paths';

// Звук нужен, если в snapshot'е шаблона проставлен звуковой сигнал (любое значение, кроме NONE).
// Сравнение регистронезависимо — так же, как для confirmationFlags ниже, бэкенд не гарантирует регистр.
// Отсутствие поля (undefined/'') трактуется как «звук не задан» — по контракту snapshot всегда на месте,
// поэтому для корректно настроенных шаблонов поле присутствует.
const isSoundNeeded = (targetAction?: { sound?: NotificationAction | string } | null): boolean => {
  const sound = String(targetAction?.sound ?? '').toUpperCase();
  return sound !== '' && sound !== NotificationAction.None;
};

// Решение о запуске звука по свойствам самого уведомления: только критические уведомления
// (прочие на экран не попадают) и только если в шаблоне включён звук.
// Глобальный вкл/выкл звука enforce'ится отдельно и реактивно — через volume в useSound (Alerts/index.tsx),
// поэтому здесь не проверяется (иначе значение захватилось бы в mount-only effect и устарело).
const shouldPlaySound = (severity: NotificationSeverity, needSound?: boolean): boolean =>
  severity === NotificationSeverity.Critical && Boolean(needSound);

// Автопереход на план по приходу уведомления: только критические события
// (SOS и прочие) с указанием плана. Уведомления без плана ведут себя как раньше.
const shouldAutoFocus = (alert: Pick<IAlert, 'severity' | 'mapId'>): boolean =>
  alert.severity === NotificationSeverity.Critical && Boolean(alert.mapId);

// Форма редактирования/создания в разделе карты: у маршрутов всех форм
// (офис/план/зона/инфраструктура/камера/сотрудник) последняя секция пути —
// create или edit. На них автофокус выключен: getOffice/getMap подменяют
// записи, к которым привязана открытая форма, и сохранение ушло бы не туда.
const isMapEditorRoute = (pathname: string): boolean => {
  if (!pathname.startsWith(paths.map.office.root)) {
    return false;
  }

  const sections = pathname
    .slice(paths.map.office.root.length)
    .split('/')
    .filter(Boolean);
  const last = sections[sections.length - 1];

  return last === 'create' || last === 'edit';
};

const convertNotificationToAlert = (notification: IEvent): IAlert => {
  const defaultNotificationClassIcon = { color: '' };
  const { color } = notificationTypesData[notification.class] || defaultNotificationClassIcon;

  return {
    uid: notification.eventUid,
    userId: notification.userId,
    type: notification.class,
    title: notification.classFriendly,
    description: notification.platformText,
    color,
    isConfirm: !Boolean(notification.confirmRead),
    date: notification.eventTime,
    severity: notification.severity,
    // Навигационные поля для перехода на карту
    areaId: notification.areaId || null,
    mapId: notification.mapId || null,
    lat: typeof notification.lat === 'number' ? notification.lat : null,
    lon: typeof notification.lon === 'number' ? notification.lon : null,
    alt: typeof notification.alt === 'number' ? notification.alt : null,
    // trackInfo: notification.trackInfo
    needSound: isSoundNeeded(notification.messages?.[0]?.templateData?.targetAction)
  };
};

const convertWSAlertToAlert = (alert: IWSAlert): IAlert => {
  const defaultNotificationClassIcon = { color: '' };
  const { color } = notificationTypesData[alert.class] || defaultNotificationClassIcon;

  const lat = typeof alert.lat === 'number' ? alert.lat : null;
  const lon = typeof alert.lon === 'number' ? alert.lon : null;
  const alt = typeof alert.alt === 'number' ? alert.alt : null;

  const flags = String(alert?.templateData?.confirmationFlags || '').toUpperCase();
  const needConfirmByFlags = flags !== 'NONE' && flags !== '';
  const needConfirm =
    typeof alert.isNeedConfirmation === 'boolean' ? alert.isNeedConfirmation : needConfirmByFlags;
  const isUnread = !alert.readTime;

  return {
    uid: alert.messageId,
    userId: alert.userId,
    type: alert.class,
    title: alert.classFriendly,
    description: alert.text || alert.neutralText || '',
    color,
    isConfirm: needConfirm && isUnread,
    date: alert.messageTime || alert.eventTime,
    severity: alert.messageSeverity as NotificationSeverity,
    officeId: alert.officeId ?? null,
    placeId: alert.placeId ?? alert.officeId ?? null,
    mapId: alert.mapId ?? null,
    areaId: alert.areaId ?? null,
    lat,
    lon,
    alt,
    trackInfo: alert.trackInfo,
    needSound: isSoundNeeded(alert?.templateData?.targetAction)
  };
};

const convertOperatorMessageToAlert = (m: any): IAlert => {
  const defaultNotificationClassIcon = { color: '' };
  const { color } = notificationTypesData[m.class] || defaultNotificationClassIcon;

  const lat = typeof m.lat === 'number' ? m.lat : null;
  const lon = typeof m.lon === 'number' ? m.lon : null;
  const alt = typeof m.alt === 'number' ? m.alt : null;

  const conf = String(m?.templateData?.confirmationFlags || '').toUpperCase();
  const isNeedConfirmation = conf !== 'NONE' && conf !== '';
  const isUnread = !m.readTime;

  return {
    uid: m.messageId || m.eventId,
    userId: m.userId,
    type: m.class,
    title: m.classFriendly,
    description: m.text || m.neutralText || '',
    color,
    isConfirm: isNeedConfirmation && isUnread,
    date: m.messageTime || m.sentTime || m.eventTime,
    severity: m.messageSeverity as NotificationSeverity,
    areaId: m.areaId ?? null,
    mapId: m.mapId ?? null,
    lat,
    lon,
    alt,
    needSound: isSoundNeeded(m?.templateData?.targetAction)
  };
};

export {
  convertNotificationToAlert,
  convertWSAlertToAlert,
  convertOperatorMessageToAlert,
  shouldPlaySound,
  shouldAutoFocus,
  isMapEditorRoute
};
